import React, { PureComponent } from "react";

import {
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  CustomizedYAxisTickRight,
  Error,
  selectFormat,
  StandardTooltip,
  Metric,
  formatDateFromData,
  isDateFromDataInISOFormat,
} from "components/Utils/Utils.js";
import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
  InsightsTickIndicator,
} from "components/Utils/InsightsUtils.js";
import { XRayTooltip } from "components/Utils/XRayTooltip/XRayTooltip.js";

import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  LabelList,
} from "recharts";

import Heading from "components/Elements/Heading";
import { getXrayTooltipPosition } from "helpers/DOM";

class DonationsBarLineChart extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  renderBars = (data) => {
    for (let i = 0; i <= data.length; i++) {
      return data.map((source, key) => (
        <Bar key={key} dataKey={source} fill={"#f89481"} />
      ));
    }
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span
        key={i}
        className="legend mt-2 mt-md-0"
        style={{ opacity: entry.opacity || 1 }}
      >
        <span
          className={
            "legend__icon " +
            (entry.type === "line" ? "legend__icon--line" : "")
          }
          style={{
            background: entry.type === "line" ? "unset" : entry.color,
            borderTop:
              entry.type === "line" ? "1px dashed " + entry.color : "unset",
          }}
        ></span>
        <span className="legend__text" style={{ color: entry.color }}>
          {entry.key}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    const legends = [
      { key: "New Donations", color: "#fc6d52", type: "rect" },
      { key: "Emails", color: "#f89481", opacity: 0.6, type: "rect" },
    ];

    return this.legendElement(legends);
  };

  getExportData = () => ({
    data: this.props.data.graph
      .filter((d) => d.new_transactions || d.visits)
      .map((d) => {
        d["new_transactions_label"] = "New Donations";
        d["visits_label"] = "Emails";
        return d;
      }),
    opts: {
      type: "chart",
      values: ["new_transactions", "visits"],
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.graph),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={400} />;
    }

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(this.props.data.graph, "YYYY/MM/DD");
    if (preparedData && !preparedData[0].average) {
      preparedData.forEach((d) => (d.average = d.yesterday));
    }

    return (
      <div ref={this.divPosition}>
        <div className="row mb-2">
          <div className="col-6 col-md-6">
            <Heading size={6} className={"mb-1"}>
              Emails
            </Heading>
            <div className="d-inline-block">
              <Metric {...data.visits} size={"md"} />
            </div>
          </div>

          <div className="col-6 col-md-6">
            <Heading size={6} className={"mb-1"}>
              New Donations
            </Heading>
            <div className="d-inline-block">
              <Metric {...data.new_donations} size={"md"} />
            </div>
          </div>
        </div>

        {this.renderLegends(preparedData)}
        <div className="d-flex mt-2">
          <ResponsiveContainer height={300} width="100%">
            <ComposedChart
              data={preparedData}
              margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
              isAnimationActive={false}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              onClick={(d) => {
                const indicator = "donationsBarLineChart";
                if (d !== null) {
                  d.indicator = indicator;
                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data && tooltip_data.visits && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[1] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[0].fill,
                                          }}
                                        ></span>
                                      )}
                                      Emails
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.visits
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data &&
                                  tooltip_data.new_transactions && (
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        {payload[0] && (
                                          <span
                                            className="standard-tooltip__icon"
                                            style={
                                              payload[1] === undefined
                                                ? {
                                                    background:
                                                      payload[0].stroke,
                                                  }
                                                : {
                                                    background:
                                                      payload[1].stroke,
                                                  }
                                            }
                                          ></span>
                                        )}
                                        New Donations
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          tooltip_data.new_transactions
                                        )}
                                      </span>
                                    </div>
                                  )}

                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />
              <Bar
                dataKey="visits"
                fill="#FDEAE6"
                yAxisId="left"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                connectNulls
                dataKey="new_transactions"
                fill="#fc6d52"
                stroke="#fc6d52"
                strokeWidth="3"
                dot={false}
                yAxisId="right"
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={250}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Line>

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
              />

              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTick />}
                width={20}
              />
              <YAxis
                yAxisId="right"
                axisLine={false}
                tickLine={false}
                orientation="right"
                tick={<CustomizedYAxisTickRight />}
                width={20}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => {
                  return (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      yAxisId="right"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                        />
                      }
                    />
                  );
                })}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
        {this.state.tooltip.activeCoordinate ? (
          <div
            className="xray-tooltip-container"
            style={{
              top: "160px",
              left:
                this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                this.state.width
                  ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                  : this.state.tooltip.activeCoordinate.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip.activeCoordinate
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              close={this.closeTooltip.bind(this)}
              insights={widgetsInsights}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

export default DonationsBarLineChart;
